/**
 * AppUpdateChecker — native-only silent update checker.
 *
 * On mount:
 *   1. If we are NOT running inside the native Capacitor WebView → render nothing.
 *   2. Compare the BUNDLED APP_VERSION_CODE (baked into version.js at build time)
 *      against the server's latest version_code. We deliberately do NOT read the
 *      native Android `versionCode` from build.gradle — bumping that field on
 *      every JS rebuild is error-prone, and in practice the actual app code is
 *      what was bundled, not what was declared in the gradle manifest. Reading
 *      version.js means a single bump_version.sh run is enough.
 *   3. GET /api/app-version → if `server > bundled` show banner (or modal if mandatory).
 *
 * Tapping "Update" opens the APK URL in the system browser (Android handles the install intent).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Download, X, Sparkles } from 'lucide-react';
import { APP_VERSION, APP_VERSION_CODE } from '../version';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = 'carloo_update_dismissed_version';
const SUPER_ADMIN_BYPASS_KEY = 'carloo_super_admin_bypass';

function isNative() {
  try {
    const cap = window.Capacitor;
    return !!(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
  } catch { return false; }
}

// Same bypass as AppVersionGuard — see that file for rationale.
async function checkSuperAdminBypass() {
  try {
    const cached = sessionStorage.getItem(SUPER_ADMIN_BYPASS_KEY);
    if (cached === '1') return true;
    if (cached === '0') return false;
    const token = localStorage.getItem('token');
    if (!token) return false;
    const r = await fetch(`${API_URL}/api/auth/is-super-admin`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) return false;
    const d = await r.json();
    const ok = !!d.is_super_admin;
    sessionStorage.setItem(SUPER_ADMIN_BYPASS_KEY, ok ? '1' : '0');
    return ok;
  } catch { return false; }
}

function absoluteUrl(u) {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  const base = (API_URL || '').replace(/\/$/, '');
  return base + (u.startsWith('/') ? u : `/${u}`);
}

export default function AppUpdateChecker() {
  const [state, setState] = useState(null); // { installed, latest, apkUrl, mandatory, changelog }
  const [dismissed, setDismissed] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const check = useCallback(async () => {
    if (!isNative()) return;
    try {
      // We trust the bundled APP_VERSION_CODE because the JS bundle is what
      // actually runs — not the native versionCode declared in build.gradle.
      const installedCode = APP_VERSION_CODE;

      const res = await fetch(`${API_URL}/api/app-version`);
      if (!res.ok) return;
      const txt = await res.text();
      let data = {};
      try { data = JSON.parse(txt); } catch { return; }
      const latestCode = parseInt(data.version_code, 10) || 0;

      if (latestCode > installedCode) {
        const prevDismiss = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
        setDismissed(!data.mandatory && prevDismiss >= latestCode);
        setState({
          installed: APP_VERSION,
          latest: data.version_name,
          apkUrl: absoluteUrl(data.apk_url),
          mandatory: !!data.mandatory,
          changelog: data.changelog || '',
          latestCode,
        });
      } else {
        // Server caught up — nothing to nag about.
        setState(null);
      }
    } catch (e) {
      // swallow — update check is best-effort
      console.warn('[update-check] failed', e);
    }
  }, []);

  useEffect(() => {
    check();
    checkSuperAdminBypass().then(setIsSuperAdmin);
    const id = setInterval(check, 6 * 60 * 60 * 1000); // re-check every 6h
    return () => clearInterval(id);
  }, [check]);

  if (!state) return null;
  // Soft banners are always dismissible. Mandatory modals are dismissible
  // only for the super-admin (one-time bypass per browser session) so a
  // misconfigured version_code can be repaired from Admin → Masters.
  if (dismissed && (!state.mandatory || isSuperAdmin)) return null;

  const openApk = async () => {
    try {
      const { App } = await import('@capacitor/app');
      // Try native openUrl (opens in system browser, which triggers the install intent on Android)
      if (App.openUrl) {
        await App.openUrl({ url: state.apkUrl });
        return;
      }
    } catch {}
    window.open(state.apkUrl, '_system') || window.open(state.apkUrl, '_blank');
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(state.latestCode));
    setDismissed(true);
  };

  // Mandatory → full-screen blocking modal
  if (state.mandatory) {
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ background: 'rgba(15,23,42,0.88)' }}
        data-testid="app-update-modal"
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="text-amber-500" size={22} />
            <h2 className="text-lg font-bold text-gray-900">Update required</h2>
          </div>
          <p className="text-sm text-gray-700 mb-1">
            A new version of Carloo Tex is available.
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Installed <strong>v{state.installed}</strong> → Latest <strong>v{state.latest}</strong>
          </p>
          {state.changelog && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4 text-xs text-gray-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
              {state.changelog}
            </div>
          )}
          <button
            onClick={openApk}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2"
            data-testid="app-update-install-btn"
          >
            <Download size={18} /> Download &amp; Install
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => setDismissed(true)}
              className="mt-2 w-full text-xs text-gray-500 hover:text-gray-800 underline"
              data-testid="app-update-superadmin-bypass-btn"
              title="Bypass once so you can fix the version_code in Admin → Masters"
            >
              Continue as Super Admin (one-time bypass)
            </button>
          )}
          <p className="text-[10px] text-gray-400 mt-2 text-center">
            This update is required. Please install to continue.
          </p>
        </div>
      </div>
    );
  }

  // Soft banner
  return (
    <div
      className="fixed top-2 left-2 right-2 z-[9998] max-w-xl mx-auto"
      data-testid="app-update-banner"
    >
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl shadow-lg px-4 py-3 flex items-center gap-3">
        <Sparkles size={20} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">Update available</p>
          <p className="text-xs opacity-90 truncate">
            v{state.installed} → v{state.latest}
            {state.changelog ? ` · ${state.changelog.split('\n')[0].slice(0, 60)}` : ''}
          </p>
        </div>
        <button
          onClick={openApk}
          className="bg-white text-emerald-700 font-semibold text-xs px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-emerald-50"
          data-testid="app-update-install-btn"
        >
          <Download size={14} /> Update
        </button>
        <button
          onClick={dismiss}
          className="text-white/80 hover:text-white p-1"
          aria-label="Dismiss"
          data-testid="app-update-dismiss-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
